import React from 'react';
import { MapPin, AlertTriangle, CheckCircle, ChevronRight, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Geofence Compliance Widget - Shows geofence violations
 */
const GeofenceComplianceWidget = ({ violations, totalCount, loading, onViewAll, onResolve }) => {
  const navigate = useNavigate();

  const handleViewAllClick = () => {
    if (onViewAll) {
      onViewAll();
    } else {
      navigate('/home/locationTracking');
    }
  };

  const handleViewUserClick = (violation, e) => {
    e.stopPropagation(); // Prevent event bubbling
    navigate('/home/locationTracking', {
      state: {
        selectedUserId: violation.user_id,
        userName: violation.full_name
      }
    });
  };
  const complianceRate = totalCount > 0
    ? Math.round(((100 - (violations?.filter(v => !v.resolved).length || 0)) / 100) * 100)
    : 100;

  const formatDistance = (meters) => {
    if (!meters) return 'N/A';
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${Math.round(meters)} m`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    const date = new Date(timeStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHrs > 0) return `${diffHrs}h ago`;
    return 'Just now';
  };

  const getSeverityColor = (distance) => {
    if (!distance) return 'gray';
    if (distance > 1000) return 'red';
    if (distance > 1000) return 'orange';
    return 'yellow';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="h-3 bg-gray-200 rounded w-full mb-3"></div>
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-14 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const unresolvedViolations = violations?.filter(v => !v.resolved) || [];

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin size={18} className="text-blue-600" />
          <h3 className="text-base font-semibold text-gray-800">Geofence Compliance</h3>
        </div>
        <button
          onClick={handleViewAllClick}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          View All <ChevronRight size={14} />
        </button>
      </div>

      {/* Compliance Rate Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-600">Compliance Rate</span>
          <span className={`text-sm font-semibold ${complianceRate >= 90 ? 'text-green-600' : complianceRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
            {complianceRate}%
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${complianceRate >= 90 ? 'bg-green-1000' : complianceRate >= 70 ? 'bg-yellow-1000' : 'bg-red-1000'}`}
            style={{ width: `${complianceRate}%` }}
          ></div>
        </div>
      </div>

      {/* Violations List */}
      {unresolvedViolations.length > 0 ? (
        <>
          <p className="text-xs text-gray-1000 mb-2">Recent Violations</p>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {unresolvedViolations.slice(0, 3).map((violation, index) => {
              const severityColor = getSeverityColor(violation.distance_meters);
              return (
                <div
                  key={violation.violation_id || index}
                  className={`flex items-center justify-between p-2 rounded-lg border ${
                    severityColor === 'red'
                      ? 'bg-red-50 border-red-200'
                      : severityColor === 'orange'
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <AlertTriangle
                      size={14}
                      className={
                        severityColor === 'red'
                          ? 'text-red-1000'
                          : severityColor === 'orange'
                          ? 'text-orange-1000'
                          : 'text-yellow-1000'
                      }
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">
                        {violation.full_name || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-1000">
                        {formatDistance(violation.distance_meters)} • {violation.location_name || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-xs text-gray-400">{formatTime(violation.violation_time)}</p>
                    </div>
                    <button
                      onClick={(e) => handleViewUserClick(violation, e)}
                      className="p-1.5 hover:bg-white rounded-md transition-colors group"
                      title="View user location tracking"
                    >
                      <Eye size={14} className="text-gray-1000 group-hover:text-blue-600" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {unresolvedViolations.length > 3 && (
            <button
              onClick={onViewAll}
              className="w-full mt-2 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              +{unresolvedViolations.length - 3} more
            </button>
          )}
        </>
      ) : (
        <div className="text-center py-4">
          <CheckCircle size={24} className="text-green-1000 mx-auto mb-2" />
          <p className="text-gray-600 text-sm font-medium">All Clear!</p>
          <p className="text-xs text-gray-400">No violations</p>
        </div>
      )}
    </div>
  );
};

export default GeofenceComplianceWidget;
